// Detect which Werkingflow app a browser-panel webview is showing, so the
// AppLoginPanel can offer one-click test-user login.
//
// The webview loads an app via devPortUrl():
//   dev:     http://<host>:<port>/<path>
//   partner: https://<port>.partner.werking.tools/<path>
// Either way the port identifies the app. PORT_TO_APP maps it to the key used
// in credentials.json (and by the /api/app-credentials endpoint).

const PORT_TO_APP: Record<number, string> = {
  3005: 'werking-noise',
  3006: 'werking-safety',
  3007: 'werking-energy',
  3008: 'werking-report',
  3009: 'engelmann-ai-hub',
  // WerkING Works (ERP, Fork von engelmann, 13.09.2026): laeuft heute nur im Haus Partner
  // (https://3016.partner.werking.tools, eigene Supabase-Instanz). Ohne diesen Eintrag galt
  // Works fuer Buehnen nicht als App — Konto-Pflicht und Login-Add-In griffen nicht
  // (Rafael, 15.09.2026, e-works-konto: a).
  3016: 'werking-works',
};

// Reverse of PORT_TO_APP: the local/partner dev port for an app key. Used by the
// login add-in's Local/Prod toggle to navigate the webview back to the local
// instance (the prod direction uses the app's productionUrl from the creds API).
const APP_TO_PORT: Record<string, number> = Object.fromEntries(
  Object.entries(PORT_TO_APP).map(([port, app]) => [app, parseInt(port, 10)]),
);
export function getDevPort(appKey: string): number | null {
  return APP_TO_PORT[appKey] ?? null;
}

// Production URLs have no port (dev/partner URLs are matched by port above), so
// map them by hostname. Covers the live custom domains and the *.vercel.app
// aliases. Keys MUST match /api/app-credentials (note: engelmann → engelmann-ai-hub).
// Only verified-live hosts are listed (e.g. safety.werking.tools does not resolve).
const HOST_TO_APP: Record<string, string> = {
  'noise.werking.tools': 'werking-noise',
  'energy.werking.tools': 'werking-energy',
  'report.werking.tools': 'werking-report',
  'werking-noise.vercel.app': 'werking-noise',
  'werking-safety.vercel.app': 'werking-safety',
  'werking-energy.vercel.app': 'werking-energy',
  'werking-report.vercel.app': 'werking-report',
  'engelmann.vercel.app': 'engelmann-ai-hub',
  'werkingflow-platform.vercel.app': 'werkingflow-platform',
};

// STAGING / Preview hosts. Vercel preview deploys (`deploy-staging <app>` → develop
// branch) get hosts of the form `<vercel-project>-<suffix>-rafael-engelmanns-projects
// .vercel.app` — the stable develop-branch domain `<project>-git-develop-…` AND the
// per-deploy `<project>-<hash>-…`. The leading segment is the Vercel PROJECT name
// (note: engelmann's project is `engelmann`, appKey `engelmann-ai-hub`). Match by the
// longest known project prefix so multi-hyphen names (`werking-energy`) resolve correctly.
//
// STAGING SPRICHT DIE DEV-BRIDGE, NICHT DIE PROD-BRIDGE (nachgemessen 08.09.2026).
// Hier stand bis dahin das Gegenteil ("same Bridge auth as production → PROD credential
// set"). Das war einmal richtig und ist es seit dem Bridge-Split nicht mehr:
// `sync-infisical-to-vercel` fuehrt AI_BRIDGE_URL (+ API-Key, Service-Token) fuer
// report/energy/noise/tools in PREVIEW_FROM_DEV_BY_PROJECT, Preview bekommt also den
// dev-Wert. Der Split kam 2026-07-24 genau deswegen: davor sprach Staging die
// PROD-Bridge mit echten Kunden an.
// Gemessen: POST /api/auth/login auf der report-develop-Domain mit dem NUR auf der
// dev-Bridge existierenden test-report@werkingflow.com → HTTP 200 (mit
// Protection-Bypass-Header, sonst faengt Vercel-SSO den Aufruf vorher ab).
// Folge fuer den Login-Zusatz: auf Staging gilt der DEV-Satz. Wer dort die
// Prod-Konten anbietet, bietet genau die an, die dort 401 liefern — und verbirgt die,
// die funktionieren.
const STAGING_HOST_SUFFIX = '-rafael-engelmanns-projects.vercel.app';
const STAGING_PROJECT_TO_APP: Record<string, string> = {
  'werking-energy': 'werking-energy',
  'werking-report': 'werking-report',
  'werking-noise': 'werking-noise',
  'werking-safety': 'werking-safety',
  'engelmann': 'engelmann-ai-hub',
  'werkingflow-platform': 'werkingflow-platform',
  // werking-tools: Testkonto tools-test (dev-Bridge) ist freigegeben, die develop-Domain
  // fehlte hier — Kachel mit konto wurde abgewiesen (FFG-Stunden-Bühne, 23.10.2026).
  'werking-tools': 'werking-tools',
};

// Stable aliases declared in packages/config/env-contracts.json. Exact host
// matching keeps unrelated preview domains out of the credential picker.
const STAGING_ALIAS_TO_APP: Record<string, string> = {
  'staging-werking-report.vercel.app': 'werking-report',
  'staging-werking-energy.vercel.app': 'werking-energy',
  // Eigene Staging-Domain ohne Vercel-SSO (KD 03.11.2026, Energy-Bühne Fr 06.11.): gleicher Staging-Stand,
  // gleiche Zugänge (dev-Satz / Konten mit umgebung 'staging').
  'staging-energy.werking.tools': 'werking-energy',
  'staging-werking-safety.vercel.app': 'werking-safety',
};

/** Resolve a Vercel preview/staging hostname to an appKey, or null. */
function detectStagingApp(hostname: string): string | null {
  if (Object.prototype.hasOwnProperty.call(STAGING_ALIAS_TO_APP, hostname)) return STAGING_ALIAS_TO_APP[hostname];
  if (!hostname.endsWith(STAGING_HOST_SUFFIX)) return null;
  const lead = hostname.slice(0, -STAGING_HOST_SUFFIX.length); // e.g. werking-energy-git-develop
  // longest project name first so `werking-energy` wins over a hypothetical `werking`
  for (const proj of Object.keys(STAGING_PROJECT_TO_APP).sort((a, b) => b.length - a.length)) {
    if (lead === proj || lead.startsWith(proj + '-')) return STAGING_PROJECT_TO_APP[proj];
  }
  return null;
}

// After Q-Login the webview navigates here, not to '/'. Reason: the apps' root
// page.tsx uses isAuthenticated() from localStorage (legacy code path) and
// redirects to /login when localStorage is empty — which it is after a fetch-
// based login that only sets the HttpOnly cookie. These paths bypass the bad
// root check; the middleware accepts the cookie on them.
const POST_LOGIN_PATH: Record<string, string> = {
  'werking-energy': '/projects',
  'werking-report': '/dashboard',
  // Works leitet Angemeldete von /dashboard nach /erp (gemessen 15.09.2026, Haus Partner).
  'werking-works': '/erp',
};
export function getPostLoginPath(appKey: string): string {
  return POST_LOGIN_PATH[appKey] ?? '/';
}

export interface DetectedApp {
  /** credentials.json key — also the /api/app-credentials key. */
  appKey: string;
  /** Dev/partner port, or null for production URLs matched by host. */
  port: number | null;
  /** True when the webview currently sits on the app's /login page. */
  isLoginPage: boolean;
  /** True for deployed URLs without a port (custom domain / *.vercel.app), also
   *  for staging. Drives the Local/Prod toggle, NOT the credential set — dafuer
   *  gibt es `credentialSet`. */
  isProduction: boolean;
  /** True specifically for a Vercel preview/staging deploy (`deploy-staging`). */
  isStaging: boolean;
  /**
   * Welcher Zugangsdaten-Satz auf DIESER Adresse ueberhaupt funktioniert.
   *
   * Nicht aus `isProduction` ableitbar: Staging ist eine deployte Adresse ohne
   * Port und trotzdem die dev-Bridge (siehe Kommentar bei STAGING_PROJECT_TO_APP).
   * Genau diese Gleichsetzung war der Fehler, den es hier vorher gab.
   */
  credentialSet: 'production' | 'dev';
  /** True, wenn die App nicht ueber Port/Host, sondern ueber die eigene Antwort
   *  auf /api/version erkannt wurde (Partner-Sandkasten, siehe unten). */
  isSandbox?: boolean;
}

/** Resolve a webview URL to an app + whether it shows the login page.
 *  Returns null for non-app URLs (Google, docs, …). */
export function detectApp(rawUrl: string): DetectedApp | null {
  if (!rawUrl) return null;
  let u: URL;
  try {
    u = new URL(rawUrl);
  } catch {
    return null;
  }

  if (!['http:', 'https:'].includes(u.protocol) || u.username || u.password) return null;

  let port: number | null = null;
  if (u.port) {
    port = parseInt(u.port, 10);
  } else {
    const m = u.hostname.match(/^(\d+)\.partner\.werking\.tools$/);
    if (m) port = parseInt(m[1], 10);
  }

  // Dev/partner URLs identify the app by port; production URLs (custom domain or
  // *.vercel.app) have no port → resolve by hostname.
  let appKey: string | undefined;
  let isStaging = false;
  if (port !== null && !Number.isNaN(port)) {
    appKey = PORT_TO_APP[port];
  } else {
    appKey = HOST_TO_APP[u.hostname];
    if (!appKey) {
      const staged = detectStagingApp(u.hostname);
      if (staged) {
        appKey = staged;
        isStaging = true;
      }
    }
    port = null;
  }
  if (!appKey) return null;

  const isLoginPage = /\/login(\/|$)/.test(u.pathname);
  // Production AND staging URLs are matched by hostname → no port → isProduction
  // (same prod credential set). isStaging additionally flags Vercel previews.
  const isProduction = port === null;
  return {
    appKey, port, isLoginPage, isProduction, isStaging,
    credentialSet: isProduction && !isStaging ? 'production' : 'dev',
  };
}

/** The partner CUI itself (not one of the apps it hosts on a port subdomain).
 *  Deliberately NOT an entry in HOST_TO_APP: those keys must resolve in
 *  /api/app-credentials, and the partner CUI has no credential set there — its
 *  accounts live in users.json with hashed passwords. The session switcher uses
 *  the admin's own session instead, so it needs only this yes/no. */
export function isPartnerCui(rawUrl: string): boolean {
  if (!rawUrl) return false;
  try {
    return new URL(rawUrl).hostname === 'partner.werking.tools';
  } catch {
    return false;
  }
}

/**
 * Synthetischer `app`-Schluessel fuer `AktivesKonto`, wenn das Konto sich NICHT
 * bei einer der WerkING-Apps anmeldet, sondern bei der Partner-CUI selbst
 * (`isPartnerCui()`). `detectApp()` liefert dafuer bewusst `null` — die CUI ist
 * keine der Apps aus credentials.json —, aber `guide.ts`s `konto`-Pflicht fuer
 * App-Kacheln (leiteBoxenAb / rezeptFehler) braucht trotzdem EINEN appKey, um
 * Konto <-> Kachel zueinander passend zu pruefen. Ohne diesen Schluessel gaebe
 * es fuer CUI-Kacheln keinen Weg, ein Konto ueber `kachel.konto` zuzuordnen und
 * per rezeptFehler validieren zu lassen (Rafael, 15.09.2026: "in jedem Fenster,
 * das die Buehne oeffnet").
 */
export const PARTNER_CUI_APP_KEY = 'partner-cui';

/*
 * PARTNER-SANDKAESTEN (Rafael, 29.09.2026, v-cui-login-addin-sandkasten-20260929:
 * „Ja, ich baue es mit Test und gebe es an den CUI-Zustaendigen")
 * ---------------------------------------------------------------------------
 * Ein Sandkasten (`build-and-start <app> sandbox`, SANDBOX_PORT) laeuft auf einem
 * frei gewaehlten Port, z. B. https://4210.partner.werking.tools. PORT_TO_APP kennt
 * ihn nicht, also blieb das Login-Add-In dort weg. Welche App dahinter steht, sagt
 * nur die App selbst: GET /api/version antwortet mit {"app":"<appKey>", …}.
 *
 * Gefragt wird NICHT aus der Webview und NICHT aus dem Client, sondern vom
 * CUI-Server (GET /api/app-sandkasten/:port → server/lib/sandkasten-app.ts). Der
 * Client reicht dafuer nur die PORTNUMMER weiter, nie eine URL — der Server baut
 * die Zieladresse (http://127.0.0.1:<port>/api/version) selbst. Hier im geteilten
 * Modul steht nur, WELCHE Adressen ueberhaupt als Sandkasten in Frage kommen und
 * welche Antwort als App gilt.
 *
 * Sandkasten = lokal, an der dev-Bridge → IMMER der dev-Satz, nie production.
 */

/** Jeder appKey, den die feste Erkennung kennt. Nur diese darf ein Sandkasten
 *  per /api/version fuer sich beanspruchen. */
export const KNOWN_APP_KEYS: ReadonlySet<string> = new Set([
  ...Object.values(PORT_TO_APP),
  ...Object.values(HOST_TO_APP),
  ...Object.values(STAGING_PROJECT_TO_APP),
  ...Object.values(STAGING_ALIAS_TO_APP),
]);

/** Portbereich, den ein Sandkasten haben darf: keine privilegierten Ports. */
export function istSandkastenPort(port: unknown): port is number {
  return typeof port === 'number' && Number.isInteger(port) && port >= 1024 && port <= 65535;
}

const LOKALE_HOSTS = new Set(['localhost', '127.0.0.1']);

/**
 * Port eines moeglichen Sandkastens, oder null. Nur zwei Adressformen des eigenen
 * Hauses: http(s)://localhost:<port> / 127.0.0.1:<port> und
 * https://<port>.partner.werking.tools. Ports, die detectApp() schon fest kennt
 * (3008 usw.), sind KEIN Sandkasten — deren Erkennung bleibt unveraendert.
 */
export function sandkastenPortAusUrl(rawUrl: string): number | null {
  if (!rawUrl) return null;
  let u: URL;
  try { u = new URL(rawUrl); } catch { return null; }
  if (!['http:', 'https:'].includes(u.protocol) || u.username || u.password) return null;
  let port: number | null = null;
  if (LOKALE_HOSTS.has(u.hostname) && u.port) {
    port = Number(u.port);
  } else if (u.protocol === 'https:' && !u.port) {
    const m = u.hostname.match(/^(\d{1,5})\.partner\.werking\.tools$/);
    if (m) port = Number(m[1]);
  }
  if (!istSandkastenPort(port)) return null;
  if (Object.prototype.hasOwnProperty.call(PORT_TO_APP, port)) return null;
  return port;
}

/** Liest aus einer /api/version-Antwort AUSSCHLIESSLICH das Feld `app` und
 *  nimmt es nur, wenn es ein bekannter appKey ist. Alles andere → null. */
export function appKeyAusVersion(body: unknown): string | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const app = (body as { app?: unknown }).app;
  if (typeof app !== 'string') return null;
  return KNOWN_APP_KEYS.has(app) ? app : null;
}

/**
 * DetectedApp fuer einen Sandkasten, dessen appKey der Server per /api/version
 * ermittelt hat. null, wenn die Adresse kein Sandkasten-Kandidat ist oder der
 * appKey unbekannt. credentialSet ist fest 'dev'.
 */
export function detectSandkastenApp(rawUrl: string, appKey: string | null | undefined): DetectedApp | null {
  if (!appKey || !KNOWN_APP_KEYS.has(appKey)) return null;
  const port = sandkastenPortAusUrl(rawUrl);
  if (port === null) return null;
  const isLoginPage = /\/login(\/|$)/.test(new URL(rawUrl).pathname);
  return {
    appKey, port, isLoginPage, isProduction: false, isStaging: false,
    credentialSet: 'dev', isSandbox: true,
  };
}
